<script lang="ts" setup>
import type {
	MapElementRef,
	MapInstanceRef
} from '~/core/service/googleMaps/types/index';
import type { Emitter, EventType } from 'mitt';

import {
	bindEvents,
	bindProps,
	excludeEventsOnAllComponents,
	getComponentEventsConfig,
	getComponentPropsConfig,
	getPropsValuesWithoutOptionsProp
} from '@/core/service/googleMaps/utils';
import mapStyle from '@/core/service/googleMaps/utils/style';
import { mapPromiseKey as mapPromiseKeySymbol } from '@/core/service/googleMaps/utils/symbols';
import { useGoogleMaps } from '~/composables/useGoogleMaps';
import { isEmpty } from '~~/utils';
import { onMounted, provide, ref, watch } from 'vue';

defineOptions({ name: 'GmapMap' });

/**
 * Map component
 * @displayName GmvMap
 * @see [source code](/guide/map.html#source-code)
 * @see [Official documentation](https://developers.google.com/maps/documentation/javascript/basics)
 * @see [Official reference](https://developers.google.com/maps/documentation/javascript/reference/map)
 */

const props = withDefaults(
	defineProps<{
		backgroundColor?: string;
		center: google.maps.LatLng | google.maps.LatLngLiteral;
		clickableIcons?: boolean;
		controlSize?: number;
		disableDefaultUI?: boolean;
		disableDoubleClickZoom?: boolean;
		draggableCursor?: string;
		draggingCursor?: string;
		draggable?: boolean;
		fullscreenControl?: boolean;
		fullscreenControlOptions?: google.maps.FullscreenControlOptions;
		gestureHandling?: 'cooperative' | 'greedy' | 'none' | 'auto';
		heading?: number;
		isFractionalZoomEnabled?: boolean;
		keyboardShortcuts?: boolean;
		mapId?: string;
		mapTypeControl?: boolean;
		mapTypeControlOptions?: google.maps.MapTypeControlOptions;
		mapTypeId?: google.maps.MapTypeId;
		maxZoom?: number;
		minZoom?: number;
		noClear?: boolean;
		restriction?: google.maps.MapRestriction;
		rotateControl?: boolean;
		rotateControlOptions?: google.maps.RotateControlOptions;
		scaleControl?: boolean;
		scaleControlOptions?: google.maps.ScaleControlOptions;
		scrollwheel?: boolean;
		streetView?: google.maps.StreetViewPanorama;
		streetViewControl?: boolean;
		streetViewControlOptions?: google.maps.StreetViewControlOptions;
		styles?: google.maps.MapTypeStyle[];
		tilt?: number;
		zoom?: number;
		zoomControl?: boolean;
		zoomControlOptions?: google.maps.ZoomControlOptions;
		mapPromiseKey?: string;
		resizeBus?: Emitter<Record<EventType, unknown>>;
		options?: { [key: string]: any };
		bounds?:
			| google.maps.LatLngBounds[]
			| google.maps.GeocoderGeometry['bounds']
			| undefined;
	}>(),
	{
		mapTypeId: globalThis?.google?.maps?.MapTypeId?.ROADMAP || 'roadmap',
		clickableIcons: true,
		disableDefaultUI: true,
		fullscreenControl: false,
		gestureHandling: 'greedy',
		keyboardShortcuts: true,
		mapTypeControl: false,
		panControl: false,
		rotateControl: false,
		scaleControl: false,
		streetViewControl: false,
		zoomControl: true,
		backgroundColor: 'none',
		mapId: 'portal-map'
	}
);

const emits = defineEmits<{
	(e: 'boundsChanged', value: google.maps.LatLngBounds | undefined): void;
	(e: 'centerChanged', value: google.maps.LatLng | undefined): void;
	(
		e: 'click',
		value: google.maps.MapMouseEvent | google.maps.IconMouseEvent
	): void;
	(
		e: 'dblClick' | 'contextMenu' | 'mouseMove' | 'mouseOut' | 'mouseOver',
		value: google.maps.MapMouseEvent
	): void;
	(
		e:
			| 'drag'
			| 'dragEnd'
			| 'dragStart'
			| 'headingChanged'
			| 'idle'
			| 'isFractionalZoomenabledChanged'
			| 'mapCapabilitiesChanged'
			| 'mapTypeIdChanged'
			| 'projectionChanged'
			| 'renderingTypeChanged'
			| 'tilesLoaded'
			| 'tiltChanged'
			| 'resize',
		value: []
	): void;
	(e: 'zoomChanged', value: number | undefined): void;
}>();

const gmpRef: MapElementRef = ref();
const mapInstance: MapInstanceRef = ref();

const { init: initGoogleMaps, api: googleMapsApi } = useGoogleMaps();

function getCenterFromBounds(
	bounds?:
		| google.maps.LatLngBounds[]
		| google.maps.GeocoderGeometry['bounds']
		| undefined
): google.maps.LatLng | null {
	if (bounds && !isEmpty(bounds) && 'getCenter' in bounds) {
		return bounds.getCenter();
	}

	return null;
}

watch(
	() => props.zoom,
	async (newVal, oldValue) => {
		if (mapInstance.value && newVal && newVal !== oldValue) {
			mapInstance.value.setZoom(newVal);
		}
	}
);

watch(
	() => props.zoomControl,
	newVal => {
		if (mapInstance.value && newVal) {
			mapInstance.value.setOptions({ zoomControl: newVal });
		}
	}
);

const resolveMapPromise = ref();
const mapPromise = new Promise<google.maps.Map>(resolve => {
	resolveMapPromise.value = resolve;
});

async function initMap() {
	// Initialize Google Maps API lazily
	await initGoogleMaps();

	if (!gmpRef.value || !googleMapsApi.value) {
		throw new Error(
			"Cannot find the template ref: 'gmpRef' or Google Maps API failed to load"
		);
	}

	const center = getCenterFromBounds(props.bounds) || props.center;

	const options = {
		...getPropsValuesWithoutOptionsProp(props),
		...props.options,
		...(center ? { center } : {})
	};

	const { Map, StyledMapType } = googleMapsApi.value
		.maps as google.maps.MapsLibrary;

	mapInstance.value = new Map(gmpRef.value, {
		...options,
		disableDefaultUI: true
	});

	const styledMapType = new StyledMapType(mapStyle);

	mapInstance.value.mapTypes.set('styled_pilar_map', styledMapType);
	mapInstance.value.setMapTypeId('styled_pilar_map');

	resolveMapPromise.value(mapInstance.value);

	const mapLayerPropsConfig = getComponentPropsConfig('GmpMap');
	const mapLayerEventsConfig = getComponentEventsConfig('GmpMap', 'auto');

	bindProps(mapLayerPropsConfig, mapInstance.value, emits as any, props);

	bindEvents(
		mapLayerEventsConfig,
		mapInstance.value,
		emits as any,
		excludeEventsOnAllComponents
	);
}

function fitBoundsToMarkers(positions: google.maps.LatLngLiteral[]) {
	if (!mapInstance.value || !positions.length) return;

	const bounds = new google.maps.LatLngBounds();

	positions.forEach(position => {
		bounds.extend(position);
	});

	mapInstance.value.fitBounds(bounds);
}

onMounted(async () => {
	await initMap();

	mapInstance.value?.addListener('zoom_changed', () => {
		emits('zoomChanged', mapInstance.value?.getZoom());
	});

	mapInstance.value?.addListener('bounds_changed', () => {
		emits('boundsChanged', mapInstance.value?.getBounds());
	});

	mapInstance.value?.addListener('dragstart', () => {
		emits('dragStart', []);
	});

	mapInstance.value?.addListener('dragend', () => {
		emits('dragEnd', []);
	});
});

provide(mapPromiseKeySymbol, mapPromise);

defineExpose({
	mapInstance,
	fitBoundsToMarkers
});
</script>

<template>
	<div
		class="relative bg-map-placeholder-image bg-cover bg-no-repeat"
		:class="{
			'bg-white bg-none': !!mapInstance
		}"
	>
		<div ref="gmpRef" class="absolute inset-0"></div>
		<div class="hidden">
			<!-- @slot The default slot is wrapped in a class that sets display: none; so by default any component you add to your map will be invisible. This is ok for most of the supplied components that interact directly with the Google map object, but it's not good if you want to bring up things like toolboxes, etc. -->
			<slot></slot>
		</div>
		<!-- @slot This slot must be used if you want to display content within the responsive wrapper for the map.  -->
		<div>
			<slot name="visible"></slot>
		</div>
	</div>
</template>
